import axios from "axios";
import store from "../store";
import { resolveTenantIdFromBrowser } from "../utils/tenant";

function resolveTenantIdForHeaders() {
  const fromStore = store.getters["tenantId/getSelectedTenant"]?.ID;
  if (fromStore != null && String(fromStore).trim() !== "") {
    return String(fromStore);
  }
  return resolveTenantIdFromBrowser() || null;
}

function shouldUseSameOriginOcrProxy() {
  if (process.env.VUE_APP_OCR_DIRECT === "1") return false;
  if (process.env.VUE_APP_OCR_SAME_ORIGIN === "0") return false;
  return process.env.VUE_APP_OCR_SAME_ORIGIN === "1";
}

export function resolveOcrApiBaseUrl() {
  const fromEnv = process.env.VUE_APP_OCR_API_URL;
  const useDirectInDev = process.env.VUE_APP_OCR_DIRECT === "1";

  if (typeof window !== "undefined") {
    if (process.env.NODE_ENV === "development" && !useDirectInDev) {
      return `${window.location.origin}/ocr-api/`;
    }
    if (shouldUseSameOriginOcrProxy()) {
      return `${window.location.origin}/ocr-api/`;
    }
  }

  if (fromEnv && String(fromEnv).trim()) {
    const url = String(fromEnv).trim();
    return url.endsWith("/") ? url : `${url}/`;
  }
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    return `${window.location.origin}/ocr-api/`;
  }
  return "https://dev-ocr.fluidsdata.ai/ocr-api/";
}

function describeFailedRequest(error) {
  const method = (error.config?.method || "request").toUpperCase();
  const url = error.config?.baseURL
    ? `${String(error.config.baseURL).replace(/\/$/, "")}/${String(error.config.url || "").replace(/^\//, "")}`
    : error.config?.url || "";
  const code = error.code ? ` (${error.code})` : "";
  const msg = error.message ? `: ${error.message}` : "";
  return `${method} ${url}${code}${msg}`.trim();
}

export function formatOcrApiError(error, fallback = "OCR service request failed.") {
  if (!error) return fallback;
  if (error.code === "ECONNABORTED") {
    return "OCR service timed out. Try again or check that the API is running.";
  }
  if (error.code === "ERR_CANCELED") {
    return "OCR request was cancelled (navigation or sign-out). Try opening OCR again.";
  }
  if (!error.response) {
    if (error instanceof ReferenceError || error instanceof TypeError) {
      return error.message || fallback;
    }
    const base = resolveOcrApiBaseUrl();
    const failed = describeFailedRequest(error);
    const sessionLike = /sessions/i.test(failed);
    const blockerHint = sessionLike
      ? " Ad or privacy extensions often block URLs containing “sessions” — allow dev-ocr.fluidsdata.ai or disable the blocker for this site."
      : "";
    const firstVisitHint = sessionLike
      ? " First OCR visit uses POST /api/v1/sessions (no session id yet). GET /sessions/current only runs when sessionStorage already has ocrSessionId."
      : "";
    return (
      `OCR request failed with no HTTP response. Failed call: ${failed}.${blockerHint}${firstVisitHint} ` +
      "Check that exact request on your FluidsData tab (e.g. dev-v3), not a direct dev-ocr tab. " +
      `API base: ${base}`
    );
  }
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || String(d)).join("; ");
  if (error.response?.status === 405) {
    return (
      "OCR API returned Method Not Allowed (405). The app host is not proxying POST /ocr-api — " +
      "nginx/IIS is serving the Vue app instead. Use direct dev-ocr (redeploy with default OCR URL), " +
      "or add a reverse proxy for /ocr-api/ on this host. On IIS with a working proxy, set VUE_APP_OCR_SAME_ORIGIN=1 at build time."
    );
  }
  if (error.response?.status === 403) {
    return (
      "OCR API returned Forbidden (403). If using dev-ocr, /ocr-api may not be deployed yet. " +
      "For local dev: run uvicorn on port 8000, set VUE_APP_OCR_PROXY_LOCAL=1 in .env, and restart npm run serve."
    );
  }
  if (error.response?.status === 502 || error.response?.status === 504) {
    return "OCR service is unavailable. If using dev-ocr, the /ocr-api path may not be deployed yet — try local uvicorn with VUE_APP_OCR_PROXY_LOCAL=1.";
  }
  return fallback;
}

const ocrApi = axios.create({
  baseURL: resolveOcrApiBaseUrl(),
  timeout: 120000,
});

ocrApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    const tenantId = resolveTenantIdForHeaders();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (tenantId) {
      config.headers["X-TenantID"] = tenantId;
    }
    const sessionId = sessionStorage.getItem("ocrSessionId");
    if (sessionId) {
      config.headers["X-OCR-Session-ID"] = sessionId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

ocrApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

export async function ensureOcrSession(subfolder = "") {
  const existing = sessionStorage.getItem("ocrSessionId");
  if (existing) {
    try {
      await ocrApi.get("api/v1/sessions/current");
      return existing;
    } catch (e) {
      if (e?.response?.status === 404) {
        sessionStorage.removeItem("ocrSessionId");
      } else {
        throw e;
      }
    }
  }
  const { data } = await ocrApi.post("api/v1/sessions", { subfolder });
  sessionStorage.setItem("ocrSessionId", data.session_id);
  return data.session_id;
}

export default ocrApi;
